import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin, ExternalLink, Facebook, Twitter, Youtube, Instagram } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success('Successfully subscribed to newsletter!');
      setEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-secondary text-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          <div>
            <div className="mb-6 flex items-center gap-3">
              <img loading="lazy" decoding="async" 
                src="/iiy-phytoventra-logo.jpg" 
                alt="IIY Phytoventra" 
                className="h-14 w-auto bg-white/95 rounded-lg p-1.5 shadow-md"
              />
              <div>
                <div className="text-xl font-bold leading-none">IIY <span className="text-emerald-300">Phytoventra</span></div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-300 mt-1">Invest In Yourself &reg;</div>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Unlocking the science of Nature for an Active Life by Investing In Yourself.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.linkedin.com/company/110393231/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on LinkedIn"
                className="w-10 h-10 bg-white/10 hover:bg-[#0077B5] rounded-full flex items-center justify-center transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61589946236557"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on Facebook"
                className="w-10 h-10 bg-white/10 hover:bg-[#1877F2] rounded-full flex items-center justify-center transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://x.com/iiyphytoventra"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on X (Twitter)"
                className="w-10 h-10 bg-white/10 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://www.youtube.com/@iiyphytoventra"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on YouTube"
                className="w-10 h-10 bg-white/10 hover:bg-[#FF0000] rounded-full flex items-center justify-center transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/iiyphytoventra"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on Instagram"
                className="w-10 h-10 bg-white/10 hover:bg-[#E4405F] rounded-full flex items-center justify-center transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://www.tiktok.com/@iiy.phytoventra"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="IIY Phytoventra on TikTok"
                className="w-10 h-10 bg-white/10 hover:bg-black rounded-full flex items-center justify-center transition-colors"
                data-testid="social-tiktok"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                  <path d="M16.5 3c.3 2.1 1.5 3.6 3.5 4v2.6c-1.3.1-2.5-.2-3.6-.8v5.6c0 3.3-2.3 5.6-5.4 5.6-2.9 0-5-2.1-5-4.9 0-2.9 2.3-5 5.2-4.8v2.7c-.3-.1-.7-.1-1-.1-1.2 0-2.1.9-2.1 2.1 0 1.3.9 2.2 2.2 2.2 1.4 0 2.4-1 2.4-2.6V3h3.4z"/>
                </svg>
              </a>
              <a
                href="https://share.google/IxYiJtV2HwSpfQGeC"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Find IIY Phytoventra on Google Maps"
                className="inline-flex items-center gap-1.5 px-3 h-10 rounded-full bg-white/10 hover:bg-white text-white hover:text-slate-900 transition-colors text-xs font-bold"
                data-testid="social-google-maps"
                title="Find us on Google"
              >
                <MapPin className="w-4 h-4" />
                <span>Find us on Google</span>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 font-heading">Quick Links</h4>
            <ul className="space-y-2">
              <Link to="/products" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-products">
                Products
              </Link>
              <Link to="/benefits" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-benefits">
                Benefits & Science
              </Link>
              <Link to="/about" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-about">
                About Us
              </Link>
              <Link to="/why-iiy-phytoventra" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-why-iiy">
                Why IIY Phytoventra
              </Link>
              <Link to="/faq" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-faq">
                FAQ
              </Link>
              <a
                href="https://blog.iiypv.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-gray-300 hover:text-primary transition-colors"
                data-testid="footer-blog"
              >
                Blog <ExternalLink className="w-3 h-3" />
              </a>
              <Link to="/discovery-call" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-discovery-call">
                Free Discovery Call
              </Link>
              <Link to="/northampton-wellness" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-northampton">
                Wellness in Northampton
              </Link>
              <Link to="/miss-goa-international" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-miss-goa">
                Miss Goa International 2026
              </Link>
              <Link to="/contact" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-contact">
                Contact
              </Link>
              <Link to="/returns" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-returns">
                Returns & Refunds
              </Link>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 font-heading">Curcumin Guides</h4>
            <ul className="space-y-2">
              <Link to="/biocurcumax" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-biocurcumax-guide">
                What is BCM-95®?
              </Link>
              <Link to="/bcm-95-explained" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-bcm95">
                BCM-95<sup>&reg;</sup> Explained
              </Link>
              <Link to="/curcumin-vs-turmeric" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-curcumin-vs-turmeric">
                Curcumin vs Turmeric
              </Link>
              <Link to="/best-curcumin-supplement-uk" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-best-curcumin-uk">
                Best Curcumin Supplement UK
              </Link>
              <Link to="/turmeric-curcumin-benefits-uk" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-curcumin-benefits-uk">
                Turmeric &amp; Curcumin Benefits UK
              </Link>
              <Link to="/joint-health-supplements-uk" className="block text-gray-300 hover:text-primary transition-colors" data-testid="footer-joint-health-uk">
                Joint Health Supplements UK
              </Link>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 font-heading">Contact Info</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <span>Northampton, United Kingdom</span>
              </li>
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <Mail className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <a href="mailto:sales@iiypv.com" className="block hover:text-primary transition-colors" data-testid="footer-sales-email">sales@iiypv.com</a>
                  <a href="mailto:support@iiypv.com" className="block hover:text-primary transition-colors" data-testid="footer-support-email">support@iiypv.com</a>
                </div>
              </li>
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <Phone className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a href="tel:+447442554379" className="hover:text-primary transition-colors" data-testid="footer-phone-number">+44 (0)74425 54379</a>
              </li>
              <li className="flex items-start gap-3 text-gray-300 text-sm">
                <Linkedin className="w-5 h-5 mt-0.5 flex-shrink-0" />
                <a 
                  href="https://www.linkedin.com/company/110393231/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  IIY on LinkedIn
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4 font-heading">Newsletter</h4>
            <p className="text-gray-300 text-sm mb-4">
              Subscribe to receive updates, access to exclusive deals, and more.
            </p>
            <form onSubmit={handleNewsletter} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="newsletter-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </div>

        {/* Medilink Midlands Membership Badge */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="https://portal.medilinkmidlands.com/member-directory/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-white/5 hover:bg-white/10 border border-white/15 rounded-2xl px-6 py-3 transition-all duration-300 group"
              data-testid="medilink-footer-badge"
            >
              <img loading="lazy" decoding="async"
                src="/medilink-midlands-member.png"
                alt="Medilink Midlands Member Logo - IIY Phytoventra"
                className="h-14 w-auto"
              />
              <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-teal-400 transition-colors" />
            </a>
          </div>

          <div className="text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} IIY Phytoventra Ltd. All rights reserved. IIY [Invest In Yourself]&reg; is a Registered Trademark.</p>
            <p className="mt-2 text-xs text-gray-500">
              <span className="font-semibold text-gray-400">IIY Phytoventra Ltd</span>
              {' \u00b7 '} Company Reg No: 10646999 {' \u00b7 '} VAT Reg No: GB503280921
            </p>
            <p className="mt-1 text-xs text-gray-500">Clinically Studied Curcumin &middot; Free UK Standard Delivery</p>
          </div>
        </div>
      </div>
    </footer>
  );
}